import React from 'react';
import { Box, Container, Typography, Chip, Divider, Card, CardContent } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

const eligible = [
  'Duplicate charges — if you were billed more than once for the same subscription period',
  'Service outage credit — if BaaStream was unavailable for more than 24 consecutive hours in a paid billing period due to causes within our control',
  'Cancellation within 7 days of your first-ever paid subscription (new subscribers only, one-time)',
  'Annual plan — pro-rated refund for whole unused months if you cancel within 30 days of annual renewal',
  'Billing errors — charges made in error by Monks Do Work (e.g., incorrect plan, currency conversion error)',
];

const notEligible = [
  'Monthly subscriptions cancelled after the 7-day new-subscriber window',
  'Partial months — monthly billing periods are not pro-rated for mid-cycle cancellations',
  'Community (free) plan — there is no charge, so no refund applies',
  'Charges older than 60 days from the date of the refund request',
  'Fees already waived, discounted, or applied as promotional or trial credits',
  'Service downtime caused by force majeure events, third-party outages, or your own infrastructure',
  'Dissatisfaction with scan results, rule coverage, or product direction (see our free trial instead)',
];

export function RefundPolicy() {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal Nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box
            component="a"
            href="/"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}
          >
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
              BaaStream
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, pb: { xs: 6, md: 8 }, pt: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Chip
            label="Legal"
            size="small"
            sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }}
          />
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 2 }}
          >
            Refund Policy
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Last updated: April 27, 2025
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, fontSize: '1.05rem', mb: 4 }}>
          BaaStream is a product of <strong>Monks Do Work</strong>. All subscriptions and billing
          are managed by Monks Do Work. We strive to be fair and transparent with every transaction.
          This Refund Policy explains when refunds are available, how to request one, and how we
          process them.
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, fontSize: '1.05rem', mb: 6 }}>
          For billing questions, contact us at{' '}
          <Box
            component="a"
            href="mailto:billing@baastream.com"
            sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            billing@baastream.com
          </Box>
          .
        </Typography>

        {/* Eligibility Cards */}
        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.5rem', md: '1.8rem' }, mb: 4 }}>
          Refund Eligibility
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            mb: 6,
          }}
        >
          <Card sx={{ border: '1px solid #dcfce7' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ color: '#16a34a' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a' }}>
                  Eligible for Refund
                </Typography>
              </Box>
              {eligible.map((item) => (
                <Box key={item} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a', mt: 0.3, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
          <Card sx={{ border: '1px solid #fee2e2' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CancelOutlinedIcon sx={{ color: '#ef4444' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                  Not Eligible for Refund
                </Typography>
              </Box>
              {notEligible.map((item) => (
                <Box key={item} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <CancelOutlinedIcon sx={{ fontSize: 16, color: '#ef4444', mt: 0.3, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* How to Request */}
        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.5rem', md: '1.8rem' }, mb: 3 }}>
          How to Request a Refund
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, mb: 3 }}>
          Email{' '}
          <Box
            component="a"
            href="mailto:billing@baastream.com"
            sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            billing@baastream.com
          </Box>{' '}
          with the subject line <strong>"Refund Request"</strong> and include:
        </Typography>
        <Box sx={{ bgcolor: '#fafafa', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 2, p: 3, mb: 3 }}>
          {[
            'The email address associated with your BaaStream account',
            'The date and amount of the charge you are disputing',
            'A brief explanation of the reason for your refund request',
            'Your transaction or invoice ID (found in your billing receipt email or dashboard Billing section)',
          ].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: i < 3 ? 1.5 : 0 }}>
              <Typography variant="body2" sx={{ color: PRIMARY, fontWeight: 700, minWidth: 20 }}>
                {i + 1}.
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" sx={{ color: '#888', lineHeight: 1.7, mb: 2 }}>
          Refund requests must be submitted within <strong>60 days</strong> of the charge date.
          Requests outside this window will not be considered unless required by applicable law.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* Processing */}
        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.5rem', md: '1.8rem' }, mb: 3 }}>
          Review & Processing Timelines
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, mb: 3 }}>
          Monks Do Work reviews all refund requests within <strong>3 business days</strong>. You
          will receive an email confirming approval or denial with a reason. Approved refunds are
          credited to the original payment method. Processing times after approval vary by provider:
        </Typography>
        {[
          { method: 'Credit / Debit Card (Stripe)', time: '5–10 business days' },
          { method: 'UPI / Net Banking / Card (Razorpay — India)', time: '5–7 business days' },
          { method: 'Bank Transfer (Enterprise invoices)', time: '7–14 business days' },
        ].map((row) => (
          <Box
            key={row.method}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.5,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#555' }}>
              {row.method}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: DARK }}>
              {row.time}
            </Typography>
          </Box>
        ))}
        <Typography variant="body2" sx={{ color: '#888', lineHeight: 1.7, mt: 2 }}>
          Monks Do Work has no control over your bank's internal processing times once we initiate
          the refund. Currency conversion fees charged by your bank or card issuer are not
          refundable by us.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* Cancellation */}
        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.5rem', md: '1.8rem' }, mb: 3 }}>
          Cancelling Your Subscription
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, mb: 3 }}>
          You may cancel your BaaStream subscription at any time from the Billing section of your
          dashboard. Cancellation stops all future charges; you retain access to your paid plan
          features until the end of the current billing period. No partial-month credits are issued
          for monthly subscriptions upon cancellation (except as described under Refund Eligibility
          above for annual plans).
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9 }}>
          Cancelling your subscription does not automatically trigger a refund. If you believe you
          are eligible for a refund at the time of cancellation, please submit a request as described
          above.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* Chargebacks */}
        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.5rem', md: '1.8rem' }, mb: 3 }}>
          Chargebacks
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9 }}>
          We encourage you to contact us at billing@baastream.com before initiating a chargeback
          with your bank or card issuer. We resolve the vast majority of billing disputes directly
          and quickly. Initiating an unwarranted chargeback may result in suspension of your account
          pending resolution.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* Questions */}
        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.5rem', md: '1.8rem' }, mb: 2 }}>
          Questions?
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9 }}>
          Contact Monks Do Work's billing team at{' '}
          <Box
            component="a"
            href="mailto:billing@baastream.com"
            sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            billing@baastream.com
          </Box>
          . We aim to resolve all billing issues fairly and promptly.
        </Typography>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2025 Monks Do Work. All rights reserved. BaaStream is a product of Monks Do Work.
            </Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              ← Back to Home
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
